/**
 * Clip Editor — Timeline overlay at the bottom of the viewport.
 *
 * Provides a time ruler, playhead, keyframe diamonds, transport controls,
 * and a "Set Key" button for authoring transform keyframes into a GLB clip.
 *
 * Auto-saves keyframes to a GLB asset whenever they change (debounced).
 *
 * Playback is delegated to the Animation behavior via onPlay/onStop/onSeek
 * callbacks so there is a single playback system driving the scene.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { TransformKeyframe } from 'trace-viewer/animation';
import type { SceneObject } from 'trace-viewer/scene';
import { TransportButtons } from './transport-buttons';
import type { Scene } from '@babylonjs/core/scene';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Animation as BabylonAnimation } from '@babylonjs/core/Animations/animation';
import { Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';

// ============================================================================
// Helpers
// ============================================================================

/** Format seconds as m:ss.t */
function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
}

/** Capture the object's current transform as a keyframe (without time) */
function captureTransform(sceneObject: SceneObject): Omit<TransformKeyframe, 'time'> {
    const t = sceneObject.transform;
    const q = t.rotationQuaternion;
    return {
        position: [t.position.x, t.position.y, t.position.z],
        rotation: q ? [q.x, q.y, q.z, q.w] : [0, 0, 0, 1],
        scale: [t.scaling.x, t.scaling.y, t.scaling.z],
    };
}

/** Snap time to nearest keyframe within a pixel tolerance */
function findNearestKeyframe(
    keyframes: readonly TransformKeyframe[],
    time: number,
    duration: number,
    trackWidth: number,
    tolerancePx: number = 8,
): number {
    const toleranceTime = (tolerancePx / trackWidth) * duration;
    let nearest = -1;
    let minDist = Infinity;
    for (let i = 0; i < keyframes.length; i++) {
        const dist = Math.abs(keyframes[i].time - time);
        if (dist < minDist && dist <= toleranceTime) {
            minDist = dist;
            nearest = i;
        }
    }
    return nearest;
}

/**
 * Build a temporary AnimationGroup from keyframes for scrub preview.
 * Used to visually apply the pose when scrubbing/seeking in the timeline.
 */
function buildPreviewGroup(
    keyframes: readonly TransformKeyframe[],
    fps: number,
    sceneObject: SceneObject,
    scene: Scene,
): AnimationGroup | null {
    if (!keyframes.length) return null;

    const target = sceneObject.transform;
    const group = new AnimationGroup("ClipPreview", scene);
    const sorted = [...keyframes].sort((a, b) => a.time - b.time);

    const posKeys = sorted.filter(kf => kf.position);
    if (posKeys.length > 0) {
        const anim = new BabylonAnimation(
            "preview_position", "position", fps,
            BabylonAnimation.ANIMATIONTYPE_VECTOR3,
            BabylonAnimation.ANIMATIONLOOPMODE_CYCLE
        );
        anim.setKeys(posKeys.map(kf => ({
            frame: kf.time * fps,
            value: new Vector3(kf.position![0], kf.position![1], kf.position![2])
        })));
        group.addTargetedAnimation(anim, target);
    }

    const rotKeys = sorted.filter(kf => kf.rotation);
    if (rotKeys.length > 0) {
        const anim = new BabylonAnimation(
            "preview_rotation", "rotationQuaternion", fps,
            BabylonAnimation.ANIMATIONTYPE_QUATERNION,
            BabylonAnimation.ANIMATIONLOOPMODE_CYCLE
        );
        anim.setKeys(rotKeys.map(kf => ({
            frame: kf.time * fps,
            value: new Quaternion(kf.rotation![0], kf.rotation![1], kf.rotation![2], kf.rotation![3])
        })));
        group.addTargetedAnimation(anim, target);
    }

    const scaleKeys = sorted.filter(kf => kf.scale);
    if (scaleKeys.length > 0) {
        const anim = new BabylonAnimation(
            "preview_scaling", "scaling", fps,
            BabylonAnimation.ANIMATIONTYPE_VECTOR3,
            BabylonAnimation.ANIMATIONLOOPMODE_CYCLE
        );
        anim.setKeys(scaleKeys.map(kf => ({
            frame: kf.time * fps,
            value: new Vector3(kf.scale![0], kf.scale![1], kf.scale![2])
        })));
        group.addTargetedAnimation(anim, target);
    }

    return group;
}

/** Derive a unique clip name that doesn't collide with existing clips */
function defaultClipName(sceneObject: SceneObject, existingNames?: string[]): string {
    const base = sceneObject.name.toLowerCase().replace(/\s+/g, '_') + '_clip';
    if (!existingNames || existingNames.length === 0) return base;
    if (!existingNames.includes(base)) return base;
    // Append incrementing number until unique
    for (let i = 2; ; i++) {
        const candidate = `${base}_${i}`;
        if (!existingNames.includes(candidate)) return candidate;
    }
}

// ============================================================================
// Component
// ============================================================================

export interface ClipEditorProps {
    sceneObject: SceneObject;
    scene: Scene;
    onSave: (keyframes: TransformKeyframe[], clipName: string, fps: number) => void | Promise<void>;
    onClose: () => void;
    /** Called externally (e.g. from K shortcut) to trigger a keyframe insert */
    onSetKeyRef?: React.MutableRefObject<(() => void) | null>;
    /** Pre-populated keyframes for editing an existing clip */
    initialKeyframes?: TransformKeyframe[];
    /** Pre-populated clip name for editing */
    initialClipName?: string;
    /** Names of clips that already exist (used to generate unique new names) */
    existingClipNames?: string[];
    /** Poll external playback state (e.g. from Animation behavior) */
    getExternalPlayback?: () => { time: number; duration: number; clipName: string; playing: boolean } | null;
    /** Trigger playback via the Animation behavior */
    onPlay?: (clipName: string) => void;
    /** Pause playback (freezes at current frame, resumable) */
    onPause?: () => void;
    /** Stop playback (resets to beginning) */
    onStop?: () => void;
    /** Seek the Animation behavior to a specific time (seconds) */
    onSeek?: (time: number) => void;
}

const AUTO_SAVE_DELAY = 500;

export const ClipEditor: React.FC<ClipEditorProps> = ({
    sceneObject, scene, onSave, onClose, onSetKeyRef,
    initialKeyframes, initialClipName, existingClipNames,
    getExternalPlayback, onPlay, onPause, onStop, onSeek,
}) => {
    const [clipName, setClipName] = useState(initialClipName ?? defaultClipName(sceneObject, existingClipNames));
    const [keyframes, setKeyframes] = useState<TransformKeyframe[]>(initialKeyframes ? [...initialKeyframes] : []);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(() => {
        if (initialKeyframes && initialKeyframes.length > 0) {
            const maxTime = Math.max(...initialKeyframes.map(kf => kf.time));
            return Math.max(maxTime + 1, 2); // At least 1s beyond last keyframe
        }
        return 5;
    });
    const [fps] = useState(30);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isScrubbing, setIsScrubbing] = useState(false);
    const [selectedKfIndex, setSelectedKfIndex] = useState(-1);
    const [isSaving, setIsSaving] = useState(false);

    const trackRef = useRef<HTMLDivElement>(null);
    const previewGroupRef = useRef<AnimationGroup | null>(null);
    const currentTimeRef = useRef(0);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Track previous sceneObject to detect selection changes
    const prevSceneObjectRef = useRef(sceneObject);

    // Reset all editor state when the target object changes
    useEffect(() => {
        if (prevSceneObjectRef.current === sceneObject) return;
        prevSceneObjectRef.current = sceneObject;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        previewGroupRef.current?.dispose();
        previewGroupRef.current = null;

        setClipName(defaultClipName(sceneObject));
        setKeyframes([]);
        setCurrentTime(0);
        setDuration(5);
        setIsPlaying(false);
        setIsScrubbing(false);
        setSelectedKfIndex(-1);
        setIsSaving(false);
        currentTimeRef.current = 0;
    }, [sceneObject, scene]);

    // Sync ref with state for render loop access
    currentTimeRef.current = currentTime;

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            previewGroupRef.current?.dispose();
            previewGroupRef.current = null;
        };
    }, [scene]);

    // Rebuild preview group when keyframes change (used for scrub/seek)
    useEffect(() => {
        previewGroupRef.current?.dispose();
        previewGroupRef.current = buildPreviewGroup(keyframes, fps, sceneObject, scene);
    }, [keyframes, fps, sceneObject, scene]);

    // ── Auto-save: debounced save whenever keyframes or clipName change ──

    useEffect(() => {
        if (keyframes.length === 0 || !clipName.trim()) return;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            setIsSaving(true);
            void (async () => {
                try {
                    await onSave([...keyframes], clipName.trim(), fps);
                } catch (e) {
                    console.error('[ClipEditor] Auto-save failed:', e);
                } finally {
                    setIsSaving(false);
                }
            })();
        }, AUTO_SAVE_DELAY);

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, [keyframes, clipName, fps, scene, onSave]);

    // ── Track external playback (from Animation behavior) ──
    // Follows the behavior's playback position whenever it's actively playing,
    // regardless of whether this editor initiated the play. Skipped during
    // scrubbing so the user retains direct control of the playhead.

    const isScrubRef = useRef(false);
    isScrubRef.current = isScrubbing;

    const isPlayingRef = useRef(false);
    isPlayingRef.current = isPlaying;

    const lastExternalTimeRef = useRef(-1);

    useEffect(() => {
        if (!getExternalPlayback) return;

        const observer = scene.onBeforeRenderObservable.add(() => {
            const state = getExternalPlayback();

            // While scrubbing, still track the external time so
            // lastExternalTimeRef stays current — otherwise the first
            // tick after scrub ends sees a stale value and snaps back.
            if (isScrubRef.current) {
                if (state) lastExternalTimeRef.current = state.time;
                return;
            }

            if (!state || state.clipName !== clipName) {
                if (isPlayingRef.current) setIsPlaying(false);
                return;
            }

            // Sync playhead:
            // 1. While actively playing (continuous tracking)
            // 2. On the playing→stopped transition (e.g. natural completion)
            // 3. When external time jumps while paused (e.g. stop after pause)
            const prevExtTime = lastExternalTimeRef.current;
            if (state.playing || isPlayingRef.current) {
                const t = Math.min(state.time, duration);
                currentTimeRef.current = t;
                setCurrentTime(t);
            } else if (prevExtTime >= 0 && state.time !== prevExtTime) {
                const t = Math.min(state.time, duration);
                currentTimeRef.current = t;
                setCurrentTime(t);
            }
            lastExternalTimeRef.current = state.time;

            // Mirror the playing state so our UI reflects it
            if (state.playing && !isPlayingRef.current) setIsPlaying(true);
            if (!state.playing && isPlayingRef.current) setIsPlaying(false);
        });

        return () => {
            scene.onBeforeRenderObservable.remove(observer);
        };
    }, [getExternalPlayback, clipName, duration, scene]);

    // ── Set Keyframe ──

    const handleSetKey = useCallback(() => {
        const time = currentTimeRef.current;
        const transform = captureTransform(sceneObject);
        const existing = [...keyframes];

        const epsilon = 0.001;
        const existingIndex = existing.findIndex(kf => Math.abs(kf.time - time) < epsilon);

        if (existingIndex >= 0) {
            existing[existingIndex] = { time, ...transform };
        } else {
            existing.push({ time, ...transform });
            existing.sort((a, b) => a.time - b.time);
        }

        setKeyframes(existing);
    }, [sceneObject, keyframes]);

    // Expose setKey to parent (for K shortcut)
    useEffect(() => {
        if (onSetKeyRef) {
            onSetKeyRef.current = handleSetKey;
        }
        return () => {
            if (onSetKeyRef) {
                onSetKeyRef.current = null;
            }
        };
    }, [handleSetKey, onSetKeyRef]);

    // ── Delete Keyframe ──

    const handleDeleteKey = useCallback(() => {
        if (!trackRef.current) return;
        const existing = [...keyframes];
        if (existing.length === 0) return;

        const trackWidth = trackRef.current.getBoundingClientRect().width;
        const idx = selectedKfIndex >= 0
            ? selectedKfIndex
            : findNearestKeyframe(keyframes, currentTime, duration, trackWidth);

        if (idx >= 0 && idx < existing.length) {
            existing.splice(idx, 1);
            setKeyframes(existing);
            setSelectedKfIndex(-1);
        }
    }, [keyframes, currentTime, duration, selectedKfIndex]);

    // ── Transport Controls (delegate to Animation behavior) ──

    const seekToTime = useCallback((time: number) => {
        const clamped = Math.max(0, Math.min(time, duration));
        setCurrentTime(clamped);
        currentTimeRef.current = clamped;

        // Apply pose via the local preview group (immediate, works pre-save)
        const group = previewGroupRef.current;
        if (group) {
            if (!group.isStarted) {
                group.start(false, 1);
                group.pause();
            }
            const frame = clamped * fps;
            const clampedFrame = Math.max(group.from, Math.min(frame, group.to));
            group.goToFrame(clampedFrame);
        }

        // Keep the Animation behavior's active group in sync so the
        // render-loop observer reads the scrubbed position (not the old one).
        // Only forward when the behavior is actively loaded with *this* clip
        // — otherwise we'd seek an unrelated old clip and fight over poses.
        if (onSeek) {
            const extState = getExternalPlayback?.();
            if (extState && extState.clipName === clipName) {
                onSeek(clamped);
            }
        }
    }, [duration, fps, onSeek, getExternalPlayback, clipName]);

    const handlePlayPause = useCallback(() => {
        if (isPlaying) {
            onPause?.();
            setIsPlaying(false);
        } else {
            // Reset to start if at or past the clip's end. Compare against
            // the animation clip's duration (from the behavior), not the
            // editor timeline duration — the user may have placed the
            // playhead past the last keyframe.
            const extState = getExternalPlayback?.();
            const clipDuration = extState?.duration ?? duration;
            if (currentTimeRef.current >= clipDuration - 0.01) {
                seekToTime(0);
            }
            onPlay?.(clipName);
            setIsPlaying(true);
        }
    }, [isPlaying, duration, seekToTime, onPlay, onPause, getExternalPlayback, clipName]);

    const handleStop = useCallback(() => {
        onStop?.();
        setIsPlaying(false);
        seekToTime(0);
    }, [onStop, seekToTime]);

    // ── Scrubber ──

    const seekFromPointer = useCallback((clientX: number) => {
        const el = trackRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        seekToTime(fraction * duration);
    }, [duration, seekToTime]);

    const handleScrubStart = useCallback((e: React.PointerEvent) => {
        // Pause playback when scrubbing (not stop — preserves position)
        if (isPlaying) {
            onPause?.();
            setIsPlaying(false);
        }
        setIsScrubbing(true);
        seekFromPointer(e.clientX);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }, [seekFromPointer, isPlaying, onPause]);

    const handleScrubMove = useCallback((e: React.PointerEvent) => {
        if (!isScrubbing) return;
        seekFromPointer(e.clientX);
    }, [isScrubbing, seekFromPointer]);

    const handleScrubEnd = useCallback(() => {
        setIsScrubbing(false);
    }, []);

    // ── Keyframe click ──

    const handleKeyframeClick = useCallback((index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedKfIndex(index);
        const kf = keyframes[index];
        if (kf) {
            seekToTime(kf.time);
        }
    }, [keyframes, seekToTime]);

    // ── Time ticks ──

    const ticks = generateTicks(duration);
    const progress = duration > 0 ? currentTime / duration : 0;

    return (
        <div style={{
            backgroundColor: '#1a1a1a',
            borderTop: '1px solid #333',
            padding: '8px 12px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            userSelect: 'none',
        }}>
            {/* Top bar: clip name + transport + time + actions */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px',
            }}>
                {/* Clip name */}
                <input
                    type="text"
                    value={clipName}
                    onChange={(e) => setClipName(e.target.value)}
                    placeholder="Clip name..."
                    style={{
                        width: '120px',
                        padding: '3px 6px',
                        backgroundColor: '#333',
                        border: '1px solid #555',
                        borderRadius: '3px',
                        color: '#fff',
                        fontSize: '11px',
                        outline: 'none',
                        flexShrink: 0,
                    }}
                />

                {/* Duration */}
                <span style={{ fontSize: '10px', color: '#888', flexShrink: 0 }}>Duration</span>
                <input
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(Math.max(0.1, parseFloat(e.target.value) || 1))}
                    min={0.1}
                    step={0.5}
                    style={{
                        width: '50px',
                        padding: '3px 4px',
                        backgroundColor: '#333',
                        border: '1px solid #555',
                        borderRadius: '3px',
                        color: '#fff',
                        fontSize: '11px',
                        outline: 'none',
                        flexShrink: 0,
                    }}
                />

                <div style={{ width: '1px', height: '16px', backgroundColor: '#444', flexShrink: 0 }} />

                {/* Transport */}
                <TransportButtons
                    isPlaying={isPlaying}
                    onPlayPause={handlePlayPause}
                    onStop={handleStop}
                />

                {/* Time display */}
                <span style={{
                    fontSize: '11px',
                    color: '#ccc',
                    fontFamily: 'monospace',
                    minWidth: '80px',
                }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>

                <div style={{ flex: 1 }} />

                {/* Actions */}
                <button onClick={handleSetKey} style={actionBtnStyle('#2979ff')} title="Set Keyframe (K)">
                    Set Key
                </button>
                <button
                    onClick={handleDeleteKey}
                    disabled={keyframes.length === 0}
                    style={actionBtnStyle('#d32f2f', keyframes.length === 0)}
                    title="Delete Keyframe"
                >
                    Delete
                </button>

                <div style={{ width: '1px', height: '16px', backgroundColor: '#444', flexShrink: 0 }} />

                {/* Save status indicator */}
                {isSaving && (
                    <span style={{ fontSize: '10px', color: '#888', flexShrink: 0 }}>Saving...</span>
                )}

                <button onClick={onClose} style={actionBtnStyle('#444')} title="Close Clip Editor">
                    Close
                </button>
            </div>

            {/* Timeline track */}
            <div
                ref={trackRef}
                onPointerDown={handleScrubStart}
                onPointerMove={handleScrubMove}
                onPointerUp={handleScrubEnd}
                onPointerCancel={handleScrubEnd}
                style={{
                    position: 'relative',
                    height: '40px',
                    backgroundColor: '#111',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    overflow: 'visible',
                    touchAction: 'none',
                }}
            >
                {/* Tick marks */}
                {ticks.map((tick, i) => (
                    <div key={i} style={{
                        position: 'absolute',
                        left: `${(tick.time / duration) * 100}%`,
                        top: 0,
                        height: tick.major ? '12px' : '6px',
                        width: '1px',
                        backgroundColor: tick.major ? '#555' : '#333',
                    }}>
                        {tick.major && (
                            <span style={{
                                position: 'absolute',
                                top: '13px',
                                left: '-12px',
                                fontSize: '9px',
                                color: '#666',
                                width: '24px',
                                textAlign: 'center',
                            }}>
                                {tick.label}
                            </span>
                        )}
                    </div>
                ))}

                {/* Keyframe diamonds */}
                {keyframes.map((kf, i) => (
                    <div
                        key={i}
                        onClick={(e) => handleKeyframeClick(i, e)}
                        style={{
                            position: 'absolute',
                            left: `${(kf.time / duration) * 100}%`,
                            top: '50%',
                            width: '10px',
                            height: '10px',
                            backgroundColor: i === selectedKfIndex ? '#ffab00' : '#ff9800',
                            border: i === selectedKfIndex ? '1px solid #fff' : '1px solid #cc7a00',
                            transform: 'translate(-50%, -50%) rotate(45deg)',
                            cursor: 'pointer',
                            zIndex: 2,
                        }}
                        title={`${formatTime(kf.time)}`}
                    />
                ))}

                {/* Playhead line */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: `${progress * 100}%`,
                    width: '2px',
                    height: '100%',
                    backgroundColor: '#fff',
                    transform: 'translateX(-1px)',
                    zIndex: 3,
                    pointerEvents: 'none',
                }} />

                {/* Playhead handle */}
                <div style={{
                    position: 'absolute',
                    top: '-4px',
                    left: `${progress * 100}%`,
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    transform: 'translateX(-4px)',
                    zIndex: 4,
                    pointerEvents: 'none',
                }} />
            </div>
        </div>
    );
};

// ============================================================================
// Tick generation
// ============================================================================

interface Tick {
    time: number;
    major: boolean;
    label?: string;
}

function generateTicks(duration: number): Tick[] {
    if (duration <= 0) return [];

    let majorInterval: number;
    if (duration <= 2) majorInterval = 0.5;
    else if (duration <= 5) majorInterval = 1;
    else if (duration <= 15) majorInterval = 2;
    else if (duration <= 30) majorInterval = 5;
    else majorInterval = 10;

    const minorInterval = majorInterval / 4;
    const ticks: Tick[] = [];

    for (let t = 0; t <= duration + 0.001; t += minorInterval) {
        const isMajor = Math.abs(t % majorInterval) < 0.001 || Math.abs(t % majorInterval - majorInterval) < 0.001;
        ticks.push({
            time: t,
            major: isMajor,
            label: isMajor ? formatTickLabel(t) : undefined,
        });
    }

    return ticks;
}

function formatTickLabel(seconds: number): string {
    if (seconds === 0) return '0s';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    if (seconds === Math.floor(seconds)) return `${seconds}s`;
    return `${seconds.toFixed(1)}s`;
}

// ============================================================================
// Button styles
// ============================================================================

function actionBtnStyle(bg: string, disabled = false): React.CSSProperties {
    return {
        padding: '4px 10px',
        backgroundColor: bg,
        border: 'none',
        borderRadius: '3px',
        color: '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '11px',
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
    };
}
